import React, { useEffect, useId, useRef } from 'react';
import { plural, t } from '@lingui/macro';

import { Box } from '@rover/kibble/core';
import Modal from '@rover/kibble/official-patterns/Modal';
import LoadingShimmer from '@rover/kibble/patterns/LoadingShimmer';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import { SM_MIN } from '@rover/kibble/styles/MediaQuery';
import { useA11yContext } from '@rover/react-lib/src/context/A11yContext';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import { useCurrentUser } from '@rover/rsdk/src/modules/Data/Core/CurrentUser';

import useSearchByName from './hooks/useSearchByName';
import { useSearchByNameExperiment } from './hooks/useSearchByNameExperiment';
import ModalSearchScreen from './screens/ModalSearchScreen';
import SearchByNameLoginRequiredScreen from './screens/SearchByNameLoginRequiredScreen';
import { SearchNameModalClosedEvent, SearchNameModalOpenedEvent } from './analyticsEvents';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialLocation?: string;
  openMethod: 'button' | 'deeplink';
};

const SearchByNameOverlay = ({
  isOpen,
  onClose,
  initialLocation,
  openMethod,
}: Props): JSX.Element | null => {
  const { isEnabled } = useSearchByNameExperiment();
  const isMobile = !useMatchMedia(`(min-width:${SM_MIN}px)`);
  const { isAuthenticated, isLoading: isAuthLoading } = useCurrentUser();
  const { state, search, reset } = useSearchByName();
  const { setScreenReaderAnnouncement } = useA11yContext();
  const headerId = useId();
  const headerText = t`Search by name`;

  const trimmedInitialLocation = initialLocation?.trim() || '';

  // Emit once per open, deferred until auth resolves so `isAuthenticated` is accurate.
  const hasEmittedOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasEmittedOpenRef.current = false;
      return;
    }
    if (isAuthLoading || hasEmittedOpenRef.current) return;
    hasEmittedOpenRef.current = true;
    emitAnalyticsEvent(
      new SearchNameModalOpenedEvent({
        openMethod,
        isAuthenticated: Boolean(isAuthenticated),
      })
    );
  }, [isOpen, isAuthLoading, isAuthenticated, openMethod]);

  // react-modal keeps the body mounted across opens, so reset on close for a fresh form.
  const resultsRenderedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen) {
      reset();
      resultsRenderedAtRef.current = null;
    }
  }, [isOpen, reset]);

  // Stamp when a non-empty results list first renders; powers dwell time on close.
  useEffect(() => {
    if (resultsRenderedAtRef.current === null && (state.results?.length ?? 0) > 0) {
      resultsRenderedAtRef.current = Date.now();
    }
  }, [state.results]);

  const wasLoadingRef = useRef(false);
  useEffect(() => {
    if (state.isLoading && !wasLoadingRef.current) {
      setScreenReaderAnnouncement(t`Searching for sitters`);
      wasLoadingRef.current = true;
      return;
    }
    if (!state.isLoading && wasLoadingRef.current) {
      wasLoadingRef.current = false;
      if (state.error) {
        setScreenReaderAnnouncement(state.error);
      } else if (state.results !== null) {
        const count = state.results.length;
        // English has no CLDR "zero" plural, so plural() would announce "0 sitters found".
        const message =
          count === 0
            ? t`No sitters found`
            : plural(count, {
                one: '# sitter found',
                other: '# sitters found',
              });
        setScreenReaderAnnouncement(message);
      }
    }
  }, [state.isLoading, state.error, state.results, setScreenReaderAnnouncement]);

  // Emit on close from the handler (not an unmount effect) so it fires once per deliberate dismiss.
  const handleClose = (): void => {
    const hasSearched = state.results !== null;
    emitAnalyticsEvent(
      new SearchNameModalClosedEvent({
        hasSearched,
        resultCount: state.results?.length ?? 0,
        isZeroResults: hasSearched && (state.results?.length ?? 0) === 0,
        searchUuid: state.searchUuid,
        isAuthenticated: Boolean(isAuthenticated),
        dwellTimeMs: resultsRenderedAtRef.current ? Date.now() - resultsRenderedAtRef.current : 0,
      })
    );
    onClose();
  };

  if (!isEnabled || isMobile) return null;

  const renderBody = (): JSX.Element => {
    if (isAuthLoading) {
      // Kibble Box's role allowlist excludes "status", so set live-region attributes directly.
      return (
        <Box
          py="4x"
          testID="search-by-name-auth-loading"
          aria-live="polite"
          aria-busy="true"
          aria-atomic="true"
        >
          <A11yHiddenBox>{t`Loading`}</A11yHiddenBox>
          <Box mb="4x">
            <LoadingShimmer width="60%" height="24px" />
          </Box>
          <Box mb="3x">
            <LoadingShimmer width="100%" height="48px" />
          </Box>
          <LoadingShimmer width="100%" height="48px" />
        </Box>
      );
    }

    if (!isAuthenticated) {
      return <SearchByNameLoginRequiredScreen />;
    }

    return (
      <ModalSearchScreen
        initialLocation={trimmedInitialLocation}
        results={state.results}
        query={state.query}
        isLoading={state.isLoading}
        error={state.error}
        searchUuid={state.searchUuid}
        onSearch={search}
      />
    );
  };

  // Modal doesn't have font-size for the header available, but 300 hits the desired 20px
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      headerText={headerText}
      headerSize="300"
      showCloseIcon
      headerId={headerId}
      contentProps={{ width: '600px', maxHeight: '80vh', sx: { overflowY: 'auto' } }}
    >
      {renderBody()}
    </Modal>
  );
};

export default SearchByNameOverlay;
